import {
  ColumnItemType,
  TableTypes,
} from "@components/Commons/ColumnSelector/ColumnSelectorDialog";
import { dashboardModeAtom, selectedColumnsAtom } from "@/atoms";
import {
  AVAILABLE_COLUMNS_PER_TABLE,
  DEFAULT_COLUMNS_PER_TABLE,
  COLUMN_OPTIONS,
  GENERAL_COLUMN_OPTIONS,
  maxMetricCountForTables,
} from "@/constants/constants";
import { useAtom } from "jotai";
import { useEffect, useMemo } from "react";
import { ACQUISITION_ROAS_METRIC } from "@lib/util-functions/acquisition-roas";

type Props = {
  table: TableTypes;
};

const useSelectedColumns = ({ table }: Props) => {
  const [dashboardMode] = useAtom(dashboardModeAtom);
  const columnOptions =
    dashboardMode === "general" ? GENERAL_COLUMN_OPTIONS : COLUMN_OPTIONS;
  const allowedTableValues = AVAILABLE_COLUMNS_PER_TABLE[table];

  const filteredTableOptions = useMemo(
    () =>
      allowedTableValues
        ? columnOptions.filter(
            (el) =>
              allowedTableValues.includes(el.value) ||
              (el.value === ACQUISITION_ROAS_METRIC &&
                allowedTableValues.includes("return_on_ad_spend"))
          )
        : columnOptions,
    [allowedTableValues, columnOptions]
  );

  const [selectedColumns, setSelectedColumns] = useAtom(selectedColumnsAtom);

  const selectedColumnsOrder = useMemo<ColumnItemType[]>(() => {
    const columns =
      (selectedColumns?.[table]
        ?.map((el) => {
          const elInOptions = filteredTableOptions.find(
            (option) => option.value === el
          );
          if (!elInOptions) return null;
          return {
            ...elInOptions,
            id: elInOptions?.value,
          } as ColumnItemType;
        })
        .filter((el) => Boolean(el)) as ColumnItemType[]) ?? [];
    return columns;
  }, [filteredTableOptions, selectedColumns, table]);

  const additionalMetrics = useMemo<ColumnItemType[]>(
    () =>
      filteredTableOptions
        .filter((el) => !selectedColumns?.[table]?.includes(el.value))
        .map((el) => ({ ...el, id: el.value })),
    [filteredTableOptions, selectedColumns, table]
  );

  const selectedColumnsForTable = selectedColumns?.[table] ?? [];

  useEffect(() => {
    // Validate and clean up stored columns - only remove columns that are no longer available
    const currentColumns = selectedColumns?.[table];
    if (!currentColumns) {
      // Table type not yet in stored state (e.g. newly added table type).
      // Initialize only the default columns, leaving optional metrics unselected.
      const defaultForTable = DEFAULT_COLUMNS_PER_TABLE[table as TableTypes];
      if (defaultForTable) {
        setSelectedColumns((current) => ({
          ...current,
          [table]: defaultForTable,
        }));
      }
      return;
    }
    if (currentColumns.length > 0) {
      const allowedValues = new Set(
        filteredTableOptions.map((opt) => opt.value)
      );

      // Filter out invalid columns and remove duplicates
      let validColumns = Array.from(
        new Set(currentColumns.filter((col) => allowedValues.has(col)))
      );

      // Apply maxMetricCountForTables limit if it exists for this table
      const maxCount = maxMetricCountForTables[table];
      if (maxCount && validColumns.length > maxCount) {
        validColumns = validColumns.slice(0, maxCount);
      }

      // Update if columns were cleaned up or trimmed
      if (
        validColumns.length !== currentColumns.length ||
        !validColumns.every((col, idx) => col === currentColumns[idx])
      ) {
        setSelectedColumns((current) => {
          return {
            ...current,
            [table]: validColumns,
          };
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const onSetNewColumnOrder = (
    columns: {
      value: (typeof filteredTableOptions)[number]["value"];
      label: (typeof filteredTableOptions)[number]["label"];
      id: (typeof filteredTableOptions)[number]["value"];
    }[]
  ) => {
    setSelectedColumns((current) => {
      return {
        ...current,
        [table]: columns.map((el) => el.value),
      };
    });
  };

  const visibleColumns = useMemo(() => {
    const columns: Record<string, boolean> = {};
    for (const column of selectedColumnsOrder) {
      columns[column.id] = true;
    }
    for (const column of additionalMetrics) {
      columns[column.id] = false;
    }
    return columns;
  }, [selectedColumnsOrder, additionalMetrics]);

  return {
    selectedColumns: selectedColumnsForTable,
    selectedColumnsOrder,
    additionalMetrics,
    setSelectedColumns,
    onSetNewColumnOrder,
    filteredTableOptions,
    visibleColumns,
  };
};

export default useSelectedColumns;
